import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Unstable_Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import AutoModeOutlinedIcon from '@mui/icons-material/AutoModeOutlined';

export default function SKURefreshSummary() {
    const [newSkus, setNewSKus] = useState(null);
    const [dmdSkus, setDmdSkus] = useState(null);
    const [dmdAlert, setDmdAlert] = useState(false);
    const [cRefSkus, setCRef] = useState(null);
    const [pRefSkus, setPRef] = useState(null);
    const [loading, setLoading] = useState(true);

    const getNewSkus = async () => {
        try {
            const res = await apiClient.get('/skucounts?daysago=3');
            const skus = res.data;
            const total = skus.reduce((sum, obj) => sum + obj["no_skus"], 0);
            setNewSKus(total.toLocaleString());
        } catch (err) {
            console.error('Error fetching new SKUs:', err);
            setNewSKus('—');
        }
    };

    const getDmdSkus = async () => {
        try {
            const res = await apiClient.get('/skurefreshcount?domain_id=1');
            const count = res.data[0]?.refreshed_count ?? 0;
            setDmdAlert(count < 2500);
            setDmdSkus(count.toLocaleString());
        } catch (err) {
            console.error('Error fetching DMD SKU refresh count:', err);
            setDmdSkus('—');
        }
    };

    const getRefreshedSkus = async () => {
        try {
            const res = await apiClient.get('/skurefreshstats');
            const cnts = res.data;
            let pending = 0;
            let completed = 0;
            
            for(let i = 0; i < cnts.length; i++) {
                const obj = cnts[i];
                if(obj["sku"] === "pending") pending = obj["total"];
                if(obj["sku"] === "completed") completed = obj["total"];
            }

            setCRef(completed.toLocaleString());
            setPRef(pending.toLocaleString());
        } catch (err) {
            console.error('Error fetching refreshed SKUs:', err);
            setCRef('—');
            setPRef('—');
        }
    };

    useEffect(() => {
        Promise.all([getNewSkus(), getDmdSkus(), getRefreshedSkus()])
            .finally(() => setLoading(false));
    }, []);

    const failedCount = [newSkus, dmdSkus, cRefSkus].filter((v) => v === '—').length;
    let feedChip = null;
    if (!loading) {
        if (failedCount === 0) {
            feedChip = { label: 'Live Feed', color: 'success' };
        } else if (failedCount < 3) {
            feedChip = { label: 'Partial Data', color: 'warning' };
        } else {
            feedChip = { label: 'Unavailable', color: 'error' };
        }
    }

    const cards = [
        {
            title: 'SKUs Found (3 Days)',
            value: newSkus,
            subtitle: 'Newly detected catalog items',
            alert: false,
        },
        {
            title: 'DMD Refreshed (24h)',
            value: dmdSkus,
            subtitle: dmdAlert ? 'Below 2,500 threshold' : null,
            alert: dmdAlert,
        },
        {
            title: 'Refreshed (24h)',
            value: cRefSkus,
            subtitle: 'Completed catalog updates',
            alert: false,
        },
        {
            title: 'Pending Refresh',
            value: pRefSkus,
            subtitle: 'Queue awaiting processing',
            alert: false,
        },
    ];

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoModeOutlinedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Recent SKU Summary
                    </Typography>
                </Box>
                {feedChip && <Chip label={feedChip.label} size="small" color={feedChip.color} sx={{ fontSize: '0.75rem' }} />}
            </Box>

            <Grid container spacing={2}>
                {cards.map((card, idx) => (
                    <Grid key={idx} xs={12} sm={6} md={3}>
                        <Paper
                            sx={{
                                p: 2.5,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                bgcolor: card.alert ? 'error.light' : 'background.paper',
                                borderColor: card.alert ? (t) => alpha(t.palette.error.main, 0.4) : 'divider',
                                transition: 'transform 0.15s ease-in-out, border-color 0.15s ease-in-out',
                                '&:hover': {
                                    borderColor: card.alert ? 'error.main' : 'secondary.dark',
                                },
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: card.alert ? 'error.contrastText' : 'text.secondary',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        letterSpacing: '0.04em',
                                        mb: 1,
                                    }}
                                >
                                    {card.title}
                                </Typography>

                                {loading ? (
                                    <Skeleton variant="text" width="60%" height={40} />
                                ) : (
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            color: card.alert ? 'error.main' : 'text.primary',
                                            letterSpacing: '-0.02em',
                                            mb: 0.5,
                                        }}
                                    >
                                        {card.value ?? '0'}
                                    </Typography>
                                )}
                            </Box>

                            <Typography
                                variant="caption"
                                sx={{
                                    color: card.alert ? 'error.contrastText' : 'text.secondary',
                                    fontSize: '0.75rem',
                                }}
                            >
                                {card.subtitle}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}